import { observer } from 'mobx-react-lite'
import { useId } from 'react'
import {
  ModalDialog,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { ButtonGroup } from '@/components/ButtonGroup'
import type { NotesRequestedSupportingItemViewModel } from '@/features/jobs/details/tax-type/info-gathering/notes/NotesRequestedSupportingItemViewModel'
import { NotesEditor } from '@/features/jobs/details/tax-type/info-gathering/notes/components/NotesEditor'

export interface NotesDialogProps {
  vm: NotesRequestedSupportingItemViewModel
  isDisabled?: boolean
  testId?: string
  saveButtonTestId?: string
  cancelButtonTestId?: string
}

/**
 * Dialog for editing notes.
 */
export const NotesDialog = observer(function NotesDialog({
  vm,
  isDisabled,
  testId,
  saveButtonTestId,
  cancelButtonTestId,
}: NotesDialogProps) {
  const formId = useId()
  const isFormDisabled = vm.save.pending || vm.isReadOnly || isDisabled

  return (
    <ModalDialog
      isOpen={vm.dialog.showing}
      onClose={vm.closeDialog}
      isDismissable={!vm.save.pending}
      sheetSizing="stretch"
      isUsingDialogScrolling
      size="lg"
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>{vm.item.heading || vm.item.name}</ModalDialogTitle>
        {vm.item.longDescription && (
          <ModalDialogDescription>
            {vm.item.longDescription}
          </ModalDialogDescription>
        )}
      </ModalDialogHeader>

      <ModalDialogContent>
        <NotesEditor formId={formId} isDisabled={isFormDisabled} vm={vm} />
      </ModalDialogContent>

      <ModalDialogFooter sticky bordered compact>
        <ButtonGroup stretch>
          <Button
            isDisabled={vm.save.pending}
            variant="outline"
            label={vm.isReadOnly ? 'Close' : 'Cancel'}
            onPress={vm.closeDialog}
            testId={cancelButtonTestId}
          />
          {!vm.isReadOnly && (
            <Button
              isPending={vm.save.pending}
              isDisabled={isDisabled}
              variant="default"
              label="Save"
              type="submit"
              form={formId}
              testId={saveButtonTestId}
            />
          )}
        </ButtonGroup>
      </ModalDialogFooter>
    </ModalDialog>
  )
})
